import React, { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { ArrowUp } from 'lucide-react';

const ScrollToTop = () => {
  const [isVisible, setIsVisible] = useState(false);

  useEffect(() => {
    const toggleVisibility = () => {
      const scrolled = document.documentElement.scrollTop;
      setIsVisible(scrolled > 300);
    };

    let ticking = false;
    const handleScroll = () => {
      if (!ticking) {
        requestAnimationFrame(() => {
          toggleVisibility();
          ticking = false;
        });
        ticking = true;
      }
    };

    window.addEventListener('scroll', handleScroll);
    toggleVisibility();

    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  const scrollToTop = () => {
    window.scrollTo({
      top: 0,
      behavior: 'smooth'
    });
  };

  return (
    <AnimatePresence>
      {isVisible && (
        <motion.button
          initial={{ opacity: 0, scale: 0.8, y: 20 }}
          animate={{ opacity: 1, scale: 1, y: 0 }}
          exit={{ opacity: 0, scale: 0.8, y: 20 }}
          whileHover={{ 
            scale: 1.05,
            y: -2,
          }}
          whileTap={{ scale: 0.95 }}
          transition={{ 
            type: "spring",
            stiffness: 400,
            damping: 17
          }}
          onClick={scrollToTop}
          className="
            fixed bottom-8 right-8 z-50 
            w-14 h-14 
            bg-white/80 dark:bg-dark-800/80
            border border-primary-200/50 dark:border-dark-600/50
            text-ocean-blue dark:text-sky-blue
            hover:text-deep-blue dark:hover:text-aqua
            hover:bg-primary-50/80 dark:hover:bg-dark-700/80
            hover:border-primary-300/60 dark:hover:border-dark-500/60
            rounded-full 
            shadow-xl hover:shadow-2xl
            hover:shadow-ocean-blue/20 dark:hover:shadow-sky-blue/20
            transition-all duration-300 ease-out
            focus:outline-none focus:ring-2 focus:ring-ocean-blue/30 dark:focus:ring-sky-blue/30
            backdrop-blur-xl
            group
          "
          aria-label="Scroll to top"
        >
          <motion.div
            whileHover={{ y: -1 }}
            transition={{ type: "spring", stiffness: 400, damping: 10 }}
            className="flex items-center justify-center w-full h-full"
          >
            <ArrowUp className="w-6 h-6 stroke-[2.5]" />
          </motion.div>

          {/* Subtle hover glow */}
          <div className="
            absolute inset-0 rounded-full 
            bg-gradient-to-r from-ocean-blue/10 to-sky-blue/10
            dark:from-sky-blue/15 dark:to-aqua/15
            opacity-0 group-hover:opacity-100 
            transition-opacity duration-300 
            blur-md scale-110
          " />

          {/* Click ripple effect */}
          <motion.div
            className="absolute inset-0 rounded-full bg-ocean-blue/20 dark:bg-sky-blue/30"
            initial={{ scale: 0, opacity: 0 }}
            whileTap={{ scale: 1.2, opacity: 1 }}
            transition={{ duration: 0.15 }}
          />
        </motion.button>
      )}
    </AnimatePresence>
  );
};

export default ScrollToTop;